import { type FC } from "react";
import { Check, File, Loader2, Trash2, XCircle } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { formatFileSize, getFileExtension } from "@/shared/utils/fileUtils";

type FileCardStatus = "idle" | "loading" | "complete" | "error";

interface FileCardProps {
  fileName: string;
  fileSize?: number;
  subtitle?: string;
  onRemove?: () => void;
  onClick?: () => void;
  isRemoving?: boolean;
  status?: FileCardStatus;
  progress?: number;
  previewUrl?: string | null;
  errorMessage?: string;
}

export const FileCard: FC<FileCardProps> = ({
  fileName,
  fileSize,
  subtitle,
  onRemove,
  onClick,
  isRemoving = false,
  status = "idle",
  progress,
  previewUrl,
  errorMessage,
}) => {
  const isError = status === "error";
  const isLoading = status === "loading";
  const isComplete = status === "complete";

  const thumbnail = previewUrl ? (
    <img src={previewUrl} alt={fileName} className="h-10 w-10 shrink-0 rounded-sm object-cover" />
  ) : (
    <File className="h-8 w-8 shrink-0 text-stone-200" strokeWidth={1} />
  );

  const isClickable = !!onClick && !isLoading && !isError;

  const fileInfo = (
    <>
      {isError ? (
        <div className="flex items-center gap-1">
          <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <p className="text-2xs text-destructive">{errorMessage || "Error"}</p>
        </div>
      ) : isLoading ? (
        <div className="mt-0.5 flex h-4 items-center">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
            <div
              className="h-full rounded-full bg-green-500 transition-all duration-300"
              style={{ width: `${progress ?? 0}%` }}
            />
          </div>
        </div>
      ) : isComplete ? (
        <div className="flex items-center gap-1 mt-0.5">
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-brand">
            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
          </span>
          <p className="text-2xs text-secondary">
            {getFileExtension(fileName)}
            {fileSize !== undefined && (
              <>
                <span className="mx-2">&middot;</span>
                {formatFileSize(fileSize)}
              </>
            )}
          </p>
        </div>
      ) : subtitle ? (
        <p className="text-2xs text-secondary mt-0.5">{subtitle}</p>
      ) : (
        <p className="text-2xs text-secondary mt-0.5">
          {getFileExtension(fileName)}
          {fileSize !== undefined && (
            <>
              <span className="mx-2">&middot;</span>
              {formatFileSize(fileSize)}
            </>
          )}
        </p>
      )}
    </>
  );

  return (
    <div
      className={cn("rounded-lg border bg-white p-4", isError ? "border-error" : "border-primary")}
    >
      <div className="flex items-center gap-3">
        {isClickable ? (
          <button
            type="button"
            onClick={onClick}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
          >
            {thumbnail}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{fileName}</p>
              {fileInfo}
            </div>
          </button>
        ) : (
          <>
            {thumbnail}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{fileName}</p>
              {fileInfo}
            </div>
          </>
        )}
        {onRemove && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onRemove}
                  disabled={isRemoving}
                  className="shrink-0 rounded-md p-1.5 text-tertiary transition-colors hover:bg-subtle hover:text-primary"
                  aria-label="Delete"
                >
                  {isRemoving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};
